import { FormGroup, Label, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { hasOverridableRestriction } from "common";
import { useFormikContext } from "formik";
import { isNil } from "lodash";
import pluralize from "pluralize";
import React, { useState } from "react";
import { CatalogItemRestriction, CatalogItemRestrictionScope, CatalogType } from "types";
import {
    getRestrictionError,
    getRestrictionScopesForType,
    getRestrictionsForType,
    getRestrictionValue,
    LABEL_BY_QUANTITY_SOURCE,
    setRestrictionValue,
} from "./AddOnFormUtils";
import { AddOnEditorFormState } from "./AddOnTypes";
import { NumberInlineValuePopover } from "@/components/Common/NumberInlineValuePopover";

interface RestrictionRow {
    scope: CatalogItemRestrictionScope;
    testId: string;
    offLabel: string;
    prefix: string;
    tail: string;
}

const RESTRICTION_ROWS: RestrictionRow[] = [
    {
        scope: CatalogItemRestrictionScope.EVENT,
        testId: "restriction-event-max",
        offLabel: "Limit add-ons per event",
        prefix: "Maximum of",
        tail: "per event",
    },
    {
        scope: CatalogItemRestrictionScope.PURCHASE_ITEM,
        testId: "restriction-reservation-max",
        offLabel: "Limit add-ons per reservation",
        prefix: "Maximum of",
        tail: "per reservation",
    },
    {
        scope: CatalogItemRestrictionScope.PRIMARY_UNIT,
        testId: "restriction-per-guest-max",
        offLabel: "Limit add-on quantity per guest",
        prefix: "Quantity must not exceed",
        tail: "per guest",
    },
];

export const AddOnRestrictionsFields = () => {
    const { values, setFieldValue } = useFormikContext<AddOnEditorFormState>();
    const [openScope, setOpenScope] = useState<CatalogItemRestrictionScope>();

    const scopes = getRestrictionScopesForType(values.type);
    if (scopes.length === 0) {
        return null;
    }

    const rows = RESTRICTION_ROWS.filter((row) => scopes.includes(row.scope));
    const restrictions = getRestrictionsForType(values.type, values.restrictions);
    const hasRestrictions = restrictions.length > 0;
    const allowOverride = hasOverridableRestriction(restrictions);
    // The API does not allow a per-event limit together with matching the guest count.
    const isMatchingQuantity = values.type === CatalogType.BOOLEAN && !isNil(values.quantitySource);
    const sourceLabel = isNil(values.quantitySource) ? undefined : LABEL_BY_QUANTITY_SOURCE[values.quantitySource];
    const eventMaxBlockedReason = isMatchingQuantity
        ? `Limits cannot be set while purchase quantity automatically matches ${sourceLabel} count`
        : undefined;

    const setRestrictions = (next: CatalogItemRestriction[]) => setFieldValue("restrictions", next);

    const handleToggle = (scope: CatalogItemRestrictionScope, isOn: boolean) => {
        setRestrictions(setRestrictionValue(restrictions, scope, isOn ? 0 : undefined));
    };

    const handleValueChange = (scope: CatalogItemRestrictionScope, value: number) => {
        setRestrictions(setRestrictionValue(restrictions, scope, value));
    };

    const handleOpenChange = (scope: CatalogItemRestrictionScope, isOpen: boolean) => {
        setOpenScope(isOpen ? scope : undefined);
    };

    const handleOverrideChange = (value: boolean) => {
        setRestrictions(restrictions.map((restriction) => ({ ...restriction, allowOverride: value })));
    };

    return (
        <FormGroup>
            <Label className="mb-4">Limits</Label>
            <div className="flex flex-col space-y-2">
                {rows.map((row) => (
                    <RestrictionRowField
                        key={row.scope}
                        isOpen={openScope === row.scope}
                        blockedReason={
                            row.scope === CatalogItemRestrictionScope.EVENT ? eventMaxBlockedReason : undefined
                        }
                        row={row}
                        restrictions={restrictions}
                        onToggle={handleToggle}
                        onValueChange={handleValueChange}
                        onOpenChange={handleOpenChange}
                    />
                ))}

                <Tooltip
                    className="inline-flex self-start"
                    content="Overselling can only be allowed when at least one add-on limit is enabled"
                    disabled={hasRestrictions}
                >
                    <Switch.Group>
                        <Switch
                            isChecked={allowOverride}
                            disabled={!hasRestrictions}
                            data-testid="restriction-can-override"
                            onChange={handleOverrideChange}
                        />
                        <Switch.Label direction="right" className={clsx(!hasRestrictions && "!cursor-not-allowed")}>
                            Allow reservationist to oversell
                        </Switch.Label>
                    </Switch.Group>
                </Tooltip>
            </div>
        </FormGroup>
    );
};

interface RestrictionRowFieldProps {
    isOpen: boolean;
    blockedReason?: string;
    row: RestrictionRow;
    restrictions: CatalogItemRestriction[];
    onToggle: (scope: CatalogItemRestrictionScope, isOn: boolean) => void;
    onValueChange: (scope: CatalogItemRestrictionScope, value: number) => void;
    onOpenChange: (scope: CatalogItemRestrictionScope, isOpen: boolean) => void;
}

const RestrictionRowField = ({
    isOpen,
    blockedReason,
    row,
    restrictions,
    onToggle,
    onValueChange,
    onOpenChange,
}: RestrictionRowFieldProps) => {
    const isBlocked = !isNil(blockedReason);
    const value = getRestrictionValue(restrictions, row.scope);

    const handleToggle = (isOn: boolean) => onToggle(row.scope, isOn);
    const handleOpenChange = (next: boolean) => onOpenChange(row.scope, next);
    const handleValueChange = (next: number) => onValueChange(row.scope, next);

    return (
        <Tooltip className="inline-flex self-start" content={blockedReason} disabled={!isBlocked}>
            <Switch.Group>
                <Switch
                    isChecked={!isNil(value)}
                    disabled={isBlocked}
                    data-testid={row.testId}
                    onChange={handleToggle}
                />
                {isNil(value) ? (
                    <Switch.Label direction="right" className={clsx(isBlocked && "!cursor-not-allowed")}>
                        {row.offLabel}
                    </Switch.Label>
                ) : (
                    <span className="ml-2 text-gray-darker" data-testid={`${row.testId}-copy`}>
                        {row.prefix}
                        <NumberInlineValuePopover
                            isOpen={isOpen}
                            value={value}
                            min="0"
                            step="1"
                            error={getRestrictionError(restrictions, row.scope)}
                            text={<var data-var="limitValue">{value}</var>}
                            classNames={{ input: `restriction-${row.scope}-input` }}
                            onOpenChange={handleOpenChange}
                            onChange={handleValueChange}
                        />
                        {pluralize("add-on", value)} {row.tail}
                    </span>
                )}
            </Switch.Group>
        </Tooltip>
    );
};
